import React,{useEffect,useState}from"react";
import{Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,Paper,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TextField,Typography}from"@mui/material";
import{useNavigate,useParams}from"react-router-dom";
import Sidebar from"../AdminPanel/Sidebar";
import TopBar from"../AdminPanel/TopBar";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import{useAlert}from"../../../context/AlertContext";

function formatNumber(value){
  const number=Number(value||0);

  return number.toLocaleString(
    "es-EC",
    {
      minimumFractionDigits:0,
      maximumFractionDigits:6
    }
  );
}

function formatDate(value){
  if(!value)return"-";

  return new Date(value).toLocaleDateString(
    "es-EC",
    {
      year:"numeric",
      month:"2-digit",
      day:"2-digit"
    }
  );
}

function formatTime(value){
  if(!value)return"-";

  return new Date(value).toLocaleTimeString(
    "es-EC",
    {
      hour:"2-digit",
      minute:"2-digit"
    }
  );
}

function fieldSx(dashboardTheme){
  return{
    "& .MuiOutlinedInput-root":{
      borderRadius:"8px",
      color:dashboardTheme.text_color,
      bgcolor:
        dashboardTheme.input_background||
        dashboardTheme.card_background,
      "& fieldset":{
        borderColor:dashboardTheme.border_color
      },
      "&:hover fieldset":{
        borderColor:dashboardTheme.primary_color
      },
      "&.Mui-focused fieldset":{
        borderColor:dashboardTheme.primary_color
      }
    },
    "& .MuiInputLabel-root":{
      color:dashboardTheme.muted_text_color
    },
    "& input":{
      color:dashboardTheme.text_color
    },
    "& textarea":{
      color:dashboardTheme.text_color
    }
  };
}

function Row({
  label,
  children,
  dashboardTheme
}){
  return(
    <Box sx={{
      display:"grid",
      gridTemplateColumns:{
        xs:"1fr",
        md:"230px minmax(0,1fr)"
      },
      borderBottom:
        `1px solid ${dashboardTheme.border_color}`
    }}>
      <Box sx={{
        bgcolor:
          dashboardTheme.theme_mode==="dark"
            ?"rgba(255,255,255,0.04)"
            :"rgba(17,24,39,0.035)",
        px:{
          xs:1.5,
          md:2
        },
        py:1.25,
        display:"flex",
        alignItems:"center"
      }}>
        <Typography sx={{
          fontSize:14,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          {label}
        </Typography>
      </Box>

      <Box sx={{
        px:{
          xs:1.5,
          md:2
        },
        py:1.25,
        display:"flex",
        alignItems:"center",
        minWidth:0
      }}>
        {children}
      </Box>
    </Box>
  );
}

export default function MovementDetail(){
  const[sidebarOpen,setSidebarOpen]=useState(true);

  const{dashboardTheme}=useDashboardTheme();
  const{showError,showSuccess}=useAlert();

  const navigate=useNavigate();
  const{number}=useParams();

  const[movement,setMovement]=useState(null);
  const[items,setItems]=useState([]);
  const[reversals,setReversals]=useState([]);
  const[loading,setLoading]=useState(true);

  const[empresaId,setEmpresaId]=useState("");
  const[empresaLoading,setEmpresaLoading]=useState(true);

  const[reverseOpen,setReverseOpen]=useState(false);
  const[reverseReason,setReverseReason]=useState("");
  const[reversing,setReversing]=useState(false);

  const token=localStorage.getItem("accessToken");

  const loadEmpresa=async()=>{
    try{
      setEmpresaLoading(true);

      const response=await fetch(
        "/api/enterprise/empresa/obtener/",
        {
          headers:{
            Authorization:`Bearer ${token}`
          }
        }
      );

      const data=await response.json();

      if(!response.ok){
        showError(
          data.error||
          data.detail||
          "No se pudo cargar la empresa."
        );
        return;
      }

      if(!data?.empresa?.id){
        showError(
          "No existe una empresa configurada."
        );
        return;
      }

      setEmpresaId(
        data.empresa.id
      );
    }catch{
      showError(
        "Error de conexión al cargar la empresa."
      );
    }finally{
      setEmpresaLoading(false);
    }
  };

  const loadDetail=async()=>{
    if(!empresaId||!number)return;

    try{
      setLoading(true);

      const response=await fetch(
        `/api/inventory/movements/${encodeURIComponent(number)}/detail/?empresa_id=${encodeURIComponent(empresaId)}`,
        {
          headers:{
            Accept:"application/json",
            Authorization:`Bearer ${token}`
          }
        }
      );

      const data=await response.json();

      if(!response.ok){
        showError(
          data.error||
          data.detail||
          "No se pudo cargar el movimiento."
        );
        return;
      }

      setMovement(
        data.movement||null
      );

      setItems(
        Array.isArray(data.items)
          ?data.items
          :[]
      );

      setReversals(
        Array.isArray(data.reversals)
          ?data.reversals
          :[]
      );
    }catch{
      showError(
        "Error de conexión al cargar el movimiento."
      );
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadEmpresa();
  },[]);

  useEffect(()=>{
    if(empresaId&&number){
      loadDetail();
    }
  },[
    empresaId,
    number
  ]);

  const reverseMovement=async()=>{
    if(!reverseReason.trim()){
      showError(
        "Debe indicar el motivo de la reversión."
      );
      return;
    }

    try{
      setReversing(true);

      const response=await fetch(
        `/api/inventory/movements/${encodeURIComponent(number)}/reverse/`,
        {
          method:"POST",
          headers:{
            Accept:"application/json",
            "Content-Type":"application/json",
            Authorization:`Bearer ${token}`
          },
          body:JSON.stringify({
            empresa_id:empresaId,
            reason:reverseReason.trim()
          })
        }
      );

      const data=await response.json();

      if(!response.ok){
        const error=
          data.error||
          data.detail;

        if(typeof error==="object"){
          showError(
            Object.values(error)
              .flat()
              .join(" ")
          );
        }else{
          showError(
            error||
            "No se pudo revertir el movimiento."
          );
        }

        return;
      }

      showSuccess(
        data.mensaje||
        "Movimiento revertido correctamente."
      );

      setReverseOpen(false);
      setReverseReason("");

      await loadDetail();
    }catch{
      showError(
        "Error de conexión al revertir el movimiento."
      );
    }finally{
      setReversing(false);
    }
  };

  return(
    <Box sx={{
      minHeight:"100vh",
      bgcolor:dashboardTheme.dashboard_background,
      display:"flex",
      color:dashboardTheme.text_color,
      overflowX:"hidden"
    }}>
      <Sidebar
        open={sidebarOpen}
        onClose={()=>
          setSidebarOpen(false)
        }
      />

      <Box sx={{
        flex:1,
        minWidth:0,
        minHeight:"100vh",
        display:"flex",
        flexDirection:"column"
      }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={()=>
            setSidebarOpen(prev=>!prev)
          }
        />

        <Box
          component="main"
          sx={{
            flex:1,
            width:"100%",
            p:{
              xs:1.5,
              sm:2,
              md:3
            }
          }}
        >
          <Box sx={{
            width:{
              xs:"100%",
              lg:"94%"
            },
            mx:"auto"
          }}>
            <Box sx={{
              display:"flex",
              justifyContent:"space-between",
              alignItems:{
                xs:"stretch",
                sm:"center"
              },
              flexDirection:{
                xs:"column",
                sm:"row"
              },
              gap:1.5,
              mb:2
            }}>
              <Box>
                <Typography sx={{
                  fontSize:{
                    xs:24,
                    sm:26,
                    md:30
                  },
                  fontWeight:600,
                  color:
                    dashboardTheme.primary_color
                }}>
                  Movimiento {number}
                </Typography>

                <Typography sx={{
                  mt:.4,
                  fontSize:13.5,
                  color:
                    dashboardTheme.muted_text_color
                }}>
                  Información y detalle del movimiento de inventario.
                </Typography>
              </Box>

              <Box sx={{
                display:"flex",
                gap:1
              }}>
                {movement?.can_reverse&&(
                  <Button
                    variant="outlined"
                    onClick={()=>
                      setReverseOpen(true)
                    }
                    sx={{
                      borderRadius:"10px",
                      textTransform:"none",
                      fontWeight:700,
                      color:"#dc2626",
                      borderColor:"#dc2626",
                      boxShadow:"none",
                      "&:hover":{
                        borderColor:"#dc2626",
                        bgcolor:
                          "rgba(220,38,38,0.08)",
                        boxShadow:"none"
                      }
                    }}
                  >
                    Revertir movimiento
                  </Button>
                )}

                <Button
                  variant="outlined"
                  onClick={()=>
                    navigate(
                      "/admin-panel/inventario"
                    )
                  }
                  sx={{
                    borderRadius:"10px",
                    textTransform:"none",
                    fontWeight:700,
                    color:
                      dashboardTheme.button_color,
                    borderColor:
                      dashboardTheme.button_color,
                    boxShadow:"none"
                  }}
                >
                  Regresar
                </Button>
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                borderRadius:"14px",
                border:
                  `1px solid ${dashboardTheme.border_color}`,
                bgcolor:
                  dashboardTheme.card_background,
                overflow:"hidden",
                boxShadow:"none"
              }}
            >
              <Box sx={{
                px:{
                  xs:1.5,
                  md:2
                },
                py:1.4,
                borderBottom:
                  `1px solid ${dashboardTheme.border_color}`,
                bgcolor:
                  dashboardTheme.theme_mode==="dark"
                    ?"rgba(255,255,255,0.025)"
                    :"rgba(17,24,39,0.02)"
              }}>
                <Typography sx={{
                  fontSize:{
                    xs:20,
                    md:23
                  },
                  fontWeight:700,
                  color:
                    dashboardTheme.primary_color
                }}>
                  Información del movimiento
                </Typography>
              </Box>

              <Box sx={{
                p:{
                  xs:1.5,
                  md:2
                }
              }}>
                {loading||empresaLoading?(
                  <Typography sx={{
                    fontSize:14,
                    color:
                      dashboardTheme.muted_text_color
                  }}>
                    Cargando información...
                  </Typography>
                ):(
                  <Box sx={{
                    borderTop:
                      `1px solid ${dashboardTheme.border_color}`,
                    borderLeft:
                      `1px solid ${dashboardTheme.border_color}`,
                    borderRight:
                      `1px solid ${dashboardTheme.border_color}`,
                    borderRadius:"10px",
                    overflow:"hidden"
                  }}>
                    <Row
                      label="Movimiento"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14,
                        fontWeight:700
                      }}>
                        {movement?.number||"-"}
                      </Typography>
                    </Row>

                    <Row
                      label="Fecha"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14
                      }}>
                        {formatDate(
                          movement?.created_at
                        )}
                      </Typography>
                    </Row>

                    <Row
                      label="Hora"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14
                      }}>
                        {formatTime(
                          movement?.created_at
                        )}
                      </Typography>
                    </Row>

                    <Row
                      label="Responsable"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14
                      }}>
                        {movement?.user?.name||
                          movement?.user?.email||
                          "-"}
                      </Typography>
                    </Row>

                    <Row
                      label="Tipo"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14
                      }}>
                        {movement?.adjustment_type_label||
                          "-"}
                      </Typography>
                    </Row>

                    <Row
                      label="Estado"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14,
                        fontWeight:700,
                        color:
                          movement?.status==="REVERTIDO"
                            ?"#dc2626"
                            :dashboardTheme.text_color
                      }}>
                        {movement?.status_label||
                          movement?.status||
                          "-"}
                      </Typography>
                    </Row>

                    <Row
                      label="Motivo"
                      dashboardTheme={dashboardTheme}
                    >
                      <Typography sx={{
                        fontSize:14
                      }}>
                        {movement?.reason||"-"}
                      </Typography>
                    </Row>

                    {movement?.status==="REVERTIDO"&&(
                      <>
                        <Row
                          label="Revertido por"
                          dashboardTheme={dashboardTheme}
                        >
                          <Typography sx={{
                            fontSize:14
                          }}>
                            {movement?.reversed_by?.name||
                              movement?.reversed_by?.email||
                              "-"}
                          </Typography>
                        </Row>

                        <Row
                          label="Fecha de reversión"
                          dashboardTheme={dashboardTheme}
                        >
                          <Typography sx={{
                            fontSize:14
                          }}>
                            {formatDate(
                              movement?.reversed_at
                            )}{" "}
                            {formatTime(
                              movement?.reversed_at
                            )}
                          </Typography>
                        </Row>

                        <Row
                          label="Motivo de reversión"
                          dashboardTheme={dashboardTheme}
                        >
                          <Typography sx={{
                            fontSize:14,
                            color:"#dc2626"
                          }}>
                            {movement?.reversal_reason||
                              "-"}
                          </Typography>
                        </Row>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mt:2,
                borderRadius:"14px",
                border:
                  `1px solid ${dashboardTheme.border_color}`,
                bgcolor:
                  dashboardTheme.card_background,
                overflow:"hidden",
                boxShadow:"none"
              }}
            >
              <Box sx={{
                px:{
                  xs:1.5,
                  md:2
                },
                py:1.4,
                borderBottom:
                  `1px solid ${dashboardTheme.border_color}`
              }}>
                <Typography sx={{
                  fontSize:{
                    xs:20,
                    md:23
                  },
                  fontWeight:700,
                  color:
                    dashboardTheme.primary_color
                }}>
                  Detalle de productos
                </Typography>
              </Box>

              <Box sx={
                dashboardTheme.tableStyles.tableArea
              }>
                <TableContainer sx={
                  dashboardTheme.tableStyles.container
                }>
                  <Table
                    size="small"
                    sx={
                      dashboardTheme.tableStyles.table
                    }
                  >
                    <TableHead>
                      <TableRow sx={
                        dashboardTheme.tableStyles.headRow
                      }>
                        <TableCell sx={{
                          ...dashboardTheme.tableStyles.headCell,
                          minWidth:220
                        }}>
                          Producto
                        </TableCell>

                        <TableCell sx={
                          dashboardTheme.tableStyles.headCell
                        }>
                          Código
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={
                            dashboardTheme.tableStyles.headCell
                          }
                        >
                          Cantidad
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={
                            dashboardTheme.tableStyles.headCell
                          }
                        >
                          Stock anterior
                        </TableCell>

                        <TableCell
                          align="right"
                          sx={
                            dashboardTheme.tableStyles.headCell
                          }
                        >
                          Stock posterior
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {items.map(item=>(
                        <TableRow
                          key={item.id}
                          sx={
                            dashboardTheme.tableStyles.bodyRow
                          }
                        >
                          <TableCell>
                            <Typography sx={{
                              fontSize:14,
                              fontWeight:700,
                              color:
                                dashboardTheme.text_color
                            }}>
                              {item.product?.name||
                                "-"}
                            </Typography>

                            {item.product?.barcode&&(
                              <Typography sx={{
                                mt:.2,
                                fontSize:12,
                                color:
                                  dashboardTheme.muted_text_color
                              }}>
                                Barras:{" "}
                                {item.product.barcode}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{
                            color:
                              dashboardTheme.text_color
                          }}>
                            {item.product?.code||
                              "-"}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              fontWeight:700,
                              color:
                                dashboardTheme.text_color
                            }}
                          >
                            {formatNumber(
                              item.quantity
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              color:
                                dashboardTheme.text_color
                            }}
                          >
                            {formatNumber(
                              item.stock_before
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              fontWeight:700,
                              color:
                                dashboardTheme.text_color
                            }}
                          >
                            {formatNumber(
                              item.stock_after
                            )}
                          </TableCell>
                        </TableRow>
                      ))}

                      {!loading&&items.length===0&&(
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            align="center"
                            sx={
                              dashboardTheme.tableStyles.emptyCell
                            }
                          >
                            No existen productos en este movimiento
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={reverseOpen}
        onClose={()=>{
          if(!reversing){
            setReverseOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:"14px",
            bgcolor:
              dashboardTheme.card_background,
            color:
              dashboardTheme.text_color,
            boxShadow:"none",
            border:
              `1px solid ${dashboardTheme.border_color}`
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight:700,
          color:"#dc2626"
        }}>
          Revertir movimiento
        </DialogTitle>

        <DialogContent>
          <Typography sx={{
            fontSize:14,
            color:
              dashboardTheme.muted_text_color,
            mb:2
          }}>
            Se crearán los movimientos contrarios. El registro original se conservará para auditoría.
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Motivo de la reversión"
            value={reverseReason}
            onChange={event=>
              setReverseReason(
                event.target.value
              )
            }
            sx={fieldSx(dashboardTheme)}
          />
        </DialogContent>

        <DialogActions sx={{
          px:3,
          pb:2
        }}>
          <Button
            disabled={reversing}
            onClick={()=>
              setReverseOpen(false)
            }
            sx={{
              textTransform:"none",
              fontWeight:700,
              color:
                dashboardTheme.text_color
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              reversing||
              !reverseReason.trim()
            }
            onClick={reverseMovement}
            sx={{
              textTransform:"none",
              fontWeight:700,
              bgcolor:"#dc2626",
              boxShadow:"none",
              "&:hover":{
                bgcolor:"#b91c1c",
                boxShadow:"none"
              }
            }}
          >
            {reversing
              ?"Revirtiendo..."
              :"Confirmar reversión"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}